import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    Typography,
} from '@mui/material';
import { CSSProperties, JSX, PointerEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Corner,
    CropState,
    FIT_SLIDER,
    AVATAR_ENCODING,
    CropEncoding,
    FAVICON_ENCODING,
    HERO_ENCODING,
    LOGO_ENCODING,
    Rect,
    cropImage,
    drawnRect,
    initialCrop,
    panBy,
    resizeFrameBy,
    sliderToZoom,
    zoomTo,
    zoomToSlider,
} from '../../library/imageCropHelper';
import { useIsCompact } from '../../library/useIsCompact';
import ResourcesIds from '../../resources/ResourcesIds';
import {
    HEADER_LOGO_MAX_HEIGHT,
    PREVIEW_BOX,
    STAGE_SIZE,
    bodyStyles,
    chequerPreviewStyles,
    controlsStyles,
    dialogActionsStyles,
    dialogTitleStyles,
    frameStyles,
    handleStyles,
    headerPreviewBackdropStyles,
    headerPreviewBarStyles,
    hintStyles,
    loadingStyles,
    PAGE_PREVIEW_BOX,
    pagePreviewLinesStyles,
    pagePreviewMarkStyles,
    pagePreviewStyles,
    previewImageStyles,
    previewLabelStyles,
    previewWindowStyles,
    previewsStyles,
    shadeStyles,
    stageImageStyles,
    stageStyles,
    warningStyles,
    zoomSliderStyles,
    HERO_PREVIEW_BOX,
    heroPreviewScrimStyles,
    heroPreviewStyles,
    heroPreviewTextStyles,
    plainPreviewStyles,
    AVATAR_PREVIEW_BOX,
    HEADER_AVATAR_SIZE,
    avatarPreviewStyles,
    headerAvatarNameStyles,
    headerAvatarPreviewStyles,
    TAB_ICON_SIZE,
    tabPreviewBackdropStyles,
    tabPreviewIconStyles,
    tabPreviewStyles,
    tabPreviewTitleStyles,
} from './logoCropDialog.styles';

/**
 * Which image is being cropped.
 *
 * Everything about the cropping itself is identical -- the stage, the frame, the canvas
 * re-encode -- and only a few things differ: the title, the hint, the save button, the words on
 * the transparency warning, and what the second preview shows the crop *on*. A separate dialog
 * would have been a copy of five hundred lines to change those.
 *
 * The second preview is the one that matters. A logo is judged against the frosted header it
 * will sit on; a watermark is judged against the page it will be printed behind, faintly,
 * under text that has to stay readable; a front-page hero is judged against the headline that
 * sits on top of it; a favicon and an avatar are judged at the 16px and 36px they are actually
 * drawn at, which is the only honest answer to "will anyone be able to tell what this is".
 * Showing any of the five another's backdrop would be showing somebody a surface their image
 * never reaches.
 *
 * Two of them also change what comes *out*. A logo and a watermark are PNG at 512px because
 * they must keep a transparent background and survive an email client; a hero is an opaque
 * photograph across the top of a page, so it is JPEG at 1920px; an avatar is a photograph
 * too, JPEG at 512px. That difference lives in `imageCropHelper`'s `CropEncoding` values,
 * not here.
 *
 * The avatar and the favicon are the two variants that change the *frame*: both are locked to
 * 1:1, because both results are put through a square or circular mask by something that is not
 * this dialog -- CSS for the avatar, the browser's own tab strip for the favicon -- and a
 * rectangle through either is cropped a second time, off-centre, by something with no idea
 * which part of it mattered. See `CropState.aspect`.
 *
 * This dialog used to live under `pages/ClinicAdmin`, which stopped being true when a patient
 * gained a reason to open it.
 */
export type CropVariant = 'logo' | 'watermark' | 'hero' | 'favicon' | 'avatar';

/**
 * Locked for an avatar and for a favicon, free for everything else. See `CropState.aspect`.
 *
 * The favicon's reason is the avatar's with a different mask: a browser draws the tab icon in
 * a square box, so a rectangular crop is letterboxed there -- cropped a second time, by
 * something with no idea which part of it mattered. Locking the frame is what makes the
 * preview honest about the 16px the reader will actually see.
 */
const aspectFor = (variant: CropVariant): number | undefined =>
    variant === 'avatar' || variant === 'favicon' ? 1 : undefined;

/** What each variant is written out as. The reasons live on the constants themselves. */
const ENCODINGS: Record<CropVariant, CropEncoding> = {
    logo: LOGO_ENCODING,
    watermark: LOGO_ENCODING,
    hero: HERO_ENCODING,
    favicon: FAVICON_ENCODING,
    avatar: AVATAR_ENCODING,
};

interface LogoCropDialogProps {
    /** The picked file, or null when the dialog is closed. */
    file: File | null;
    isUploading: boolean;
    /** Defaults to the logo, which is what every existing caller wants. */
    variant?: CropVariant;
    onCancel: () => void;
    onConfirm: (cropped: File) => void;
}

/**
 * How faint and how wide the watermark preview draws the mark.
 *
 * The defaults from `models/tenantBranding`, deliberately fixed here rather than wired to
 * the clinic's own sliders: this dialog is for choosing a crop, and the fine tuning happens
 * against the live preview on the branding card where both sliders are. What it has to show
 * is the shape of the thing on a page, which does not depend on the exact percentage.
 */
const PREVIEW_OPACITY = 0.1;

const PREVIEW_MARK_WIDTH = 0.6;

/** The zoom slider's one tick, at fit. Declared out here so it is not a new array per render. */
const FIT_MARK = [{ value: FIT_SLIDER }];

/** Which corner a pointer grabbed, or the image itself. */
type DragMode = Corner | 'pan';

interface Drag {
    pointerId: number;
    x: number;
    y: number;
    mode: DragMode;
    /** Whichever element took the pointer capture, so the release goes back to it. */
    captured: Element | null;
}

const CORNERS: Corner[] = ['nw', 'ne', 'se', 'sw'];

/**
 * One key per variant, named rather than positional.
 *
 * It took three arguments in order once, which at four became four chances per call site to
 * put `hero` where `avatar` belonged and produce a dialog titled after the wrong thing.
 */
const cropKey = (variant: CropVariant, keys: Record<CropVariant, string>): string => keys[variant];

/**
 * Where a preview should draw the image so the visible window is exactly the crop frame.
 *
 * Cheaper than it looks, and deliberately not a canvas: re-encoding the crop on every
 * pointer move would be hundreds of PNG encodes per drag. Two `overflow: hidden` boxes with
 * a negatively-offset image show the same thing for free, and update on the same state
 * change as the stage.
 *
 * `k` takes the smaller of the two ratios, which is the `maxWidth` + `maxHeight` pair the
 * real header applies -- so the header preview also shows "this logo is so wide it gets
 * capped on height", rather than only ever showing the height cap.
 */
const previewGeometry = (
    crop: CropState,
    maxWidth: number,
    maxHeight: number
): { window: CSSProperties; image: CSSProperties } => {
    const drawn: Rect = drawnRect(crop);

    const k: number = Math.min(maxWidth / crop.frame.width, maxHeight / crop.frame.height);

    return {
        window: {
            width: crop.frame.width * k,
            height: crop.frame.height * k,
        },
        image: {
            width: drawn.width * k,
            height: drawn.height * k,
            left: (drawn.x - crop.frame.x) * k,
            top: (drawn.y - crop.frame.y) * k,
        },
    };
};

/**
 * Where the stage draws the image: exactly `drawnRect`, never a second opinion about it.
 *
 * The frame, the shading and the pointer maths are all in those coordinates, so an image
 * drawn anywhere else is an image the admin is framing by eye against numbers that describe
 * something slightly different. `previewWindowStyles` paints no background of its own, so
 * where the frame runs past the image the preview shows its own backdrop through the gap --
 * the chequerboard in one, the real header bar in the other, which is what transparency is.
 */
const drawnStyle = (crop: CropState): CSSProperties => {
    const drawn: Rect = drawnRect(crop);

    return { left: drawn.x, top: drawn.y, width: drawn.width, height: drawn.height };
};

/** Where a corner handle sits, in stage px, centred on its corner. */
const handlePosition = (crop: CropState, corner: Corner): CSSProperties => {
    const isLeft: boolean = corner === 'nw' || corner === 'sw';
    const isTop: boolean = corner === 'nw' || corner === 'ne';

    return {
        left: (isLeft ? crop.frame.x : crop.frame.x + crop.frame.width) - 9,
        top: (isTop ? crop.frame.y : crop.frame.y + crop.frame.height) - 9,
        cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
    };
};

export const LogoCropDialog = ({
    file,
    isUploading,
    variant = 'logo',
    onCancel,
    onConfirm,
}: LogoCropDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact: boolean = useIsCompact();
    const stage = isCompact ? STAGE_SIZE.compact : STAGE_SIZE.desktop;

    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [crop, setCrop] = useState<CropState | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const [isCropping, setIsCropping] = useState<boolean>(false);

    const drag = useRef<Drag | null>(null);

    /**
     * The dialog owns this URL. It cannot borrow the one `loadImage` makes, because that one
     * is revoked before the image it resolves is handed back.
     */
    useEffect(() => {
        if (!file) {
            setObjectUrl(null);
            return;
        }

        const url: string = URL.createObjectURL(file);

        setObjectUrl(url);
        setCrop(null);
        setFailed(null);

        return () => URL.revokeObjectURL(url);
    }, [file]);

    /**
     * Re-frames when the stage changes size, which happens when the viewport crosses the
     * compact breakpoint -- a rotated phone, a dragged window edge.
     *
     * `crop.stage` is captured once, at decode, and every number in `CropState` is in the
     * stage's pixels. Let the box resize under them and the drawn image and the crop frame
     * stop corresponding to the box they are painted in. The crop is reset rather than
     * rescaled because the two stages differ in aspect as well as size, so there is no
     * honest way to carry a frame across; starting again on a box the admin can actually
     * see beats keeping a selection that no longer means what it shows.
     */
    useEffect(() => {
        setCrop((state) => (state ? initialCrop(state.natural, stage, aspectFor(variant)) : state));
    }, [stage.width, stage.height]);

    const onImageLoad = (event: SyntheticEvent<HTMLImageElement>): void => {
        const { naturalWidth, naturalHeight } = event.currentTarget;

        // A format the browser will not decode -- and an SVG with no intrinsic size --
        // reports zero here. Without this guard the dialog would sit loading forever.
        if (naturalWidth > 0 && naturalHeight > 0) {
            setCrop(initialCrop({ width: naturalWidth, height: naturalHeight }, stage,
                aspectFor(variant)));
        } else {
            setFailed(translate(ResourcesIds.BRANDING_LOGO_CROP_UNREADABLE));
        }
    };

    const startDrag = (mode: DragMode) => (event: PointerEvent<HTMLElement>): void => {
        if (!crop) {
            return;
        }

        if (mode !== 'pan') {
            event.stopPropagation();
        }

        const target: HTMLElement = event.currentTarget;

        drag.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            mode,
            captured: null,
        };

        // An enhancement, not a requirement: jsdom has no pointer capture at all, and a
        // synthetic pointer id that never went down makes a real browser throw.
        try {
            target.setPointerCapture?.(event.pointerId);
            drag.current.captured = target;
        } catch {
            // Left uncaptured; the stage still sees the moves by bubbling.
        }
    };

    /**
     * Lives on the stage alone, never on a handle.
     *
     * A handle sits inside the stage, so a move over one bubbles through both -- and two
     * handlers reading the same render's `crop` would have the second undo the first, since
     * by then the delta is zero but the state it computes from is still the old one. Hence
     * also the updater form below: the arithmetic is always applied to the newest state
     * rather than to whatever this closure captured.
     */
    const onPointerMove = (event: PointerEvent<HTMLElement>): void => {
        const current: Drag | null = drag.current;

        if (!current || current.pointerId !== event.pointerId) {
            return;
        }

        const dx: number = event.clientX - current.x;
        const dy: number = event.clientY - current.y;

        current.x = event.clientX;
        current.y = event.clientY;

        setCrop((state) => {
            if (!state) {
                return state;
            }

            return current.mode === 'pan'
                ? panBy(state, dx, dy)
                : resizeFrameBy(state, current.mode, dx, dy);
        });
    };

    /**
     * The release has to go to whichever element took the capture -- which is a handle, not
     * the stage this listener sits on, and asking the wrong element to release throws.
     */
    const endDrag = (event: PointerEvent<HTMLElement>): void => {
        const captured: Element | null = drag.current?.captured ?? null;

        if (captured && captured.hasPointerCapture?.(event.pointerId)) {
            captured.releasePointerCapture?.(event.pointerId);
        }

        drag.current = null;
    };

    const onSave = async (): Promise<void> => {
        if (!file || !crop) {
            return;
        }

        setIsCropping(true);
        setFailed(null);

        const cropped: File | null = await cropImage(file, crop, ENCODINGS[variant]);

        setIsCropping(false);

        if (!cropped) {
            setFailed(translate(ResourcesIds.BRANDING_LOGO_CROP_ERROR));
            return;
        }

        onConfirm(cropped);
    };

    const isOpaque: boolean = !!file && file.type !== 'image/png';
    const isBusy: boolean = isUploading || isCropping;

    const isWatermark: boolean = variant === 'watermark';
    const isHero: boolean = variant === 'hero';
    const isFavicon: boolean = variant === 'favicon';
    const isAvatar: boolean = variant === 'avatar';

    // Both avatar previews are square, so one geometry drives them and the smaller box is
    // just the same crop drawn at the size the app bar actually uses.
    const avatar = crop
        ? previewGeometry(crop, AVATAR_PREVIEW_BOX.width, AVATAR_PREVIEW_BOX.height)
        : null;
    const headerAvatar = crop
        ? previewGeometry(crop, HEADER_AVATAR_SIZE, HEADER_AVATAR_SIZE)
        : null;

    const chequer = crop ? previewGeometry(crop, PREVIEW_BOX.width, PREVIEW_BOX.height) : null;

    // The real 16px, not a scaled stand-in. The crop is square-locked, so one square window
    // is the whole geometry -- and what it shows is exactly what a tab will show.
    const tabIcon = crop ? previewGeometry(crop, TAB_ICON_SIZE, TAB_ICON_SIZE) : null;

    // Bounded by the band rather than fitted inside it: the page draws a hero with
    // `background-size: cover`, so the preview has to overflow its box the same way or it
    // would promise letterboxing the page never produces.
    const hero = crop
        ? previewGeometry(crop, HERO_PREVIEW_BOX.width, HERO_PREVIEW_BOX.height)
        : null;
    const header = crop ? previewGeometry(crop, 240, HEADER_LOGO_MAX_HEIGHT) : null;

    // Bounded on both axes, so a mark far taller than it is wide is fitted by its height --
    // which is what WatermarkResolver does on the server, and the preview has to agree with
    // the renderer or it is worse than no preview.
    const page = crop
        ? previewGeometry(
            crop,
            PAGE_PREVIEW_BOX.width * PREVIEW_MARK_WIDTH,
            PAGE_PREVIEW_BOX.height * PREVIEW_MARK_WIDTH
        )
        : null;

    // The left-hand preview: what the crop is, on whichever backdrop answers a question about
    // this kind of image. Picked once rather than at each of the three places it is read.
    const firstPreview = isHero ? hero : isAvatar ? avatar : chequer;
    const firstPreviewStyles = isHero
        ? plainPreviewStyles
        : isAvatar
            ? avatarPreviewStyles
            : chequerPreviewStyles;

    return (
        <Dialog open={!!file} onClose={onCancel} fullScreen={isCompact} maxWidth="md" fullWidth>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(cropKey(variant, {
                    logo: ResourcesIds.BRANDING_LOGO_CROP_TITLE,
                    watermark: ResourcesIds.BRANDING_WATERMARK_CROP_TITLE,
                    hero: ResourcesIds.HOME_HERO_CROP_TITLE,
                    favicon: ResourcesIds.BRANDING_FAVICON_CROP_TITLE,
                    avatar: ResourcesIds.PROFILE_PICTURE_CROP_TITLE,
                }))}
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" sx={hintStyles}>
                    {translate(cropKey(variant, {
                        logo: ResourcesIds.BRANDING_LOGO_CROP_HINT,
                        watermark: ResourcesIds.BRANDING_LOGO_CROP_HINT,
                        hero: ResourcesIds.BRANDING_LOGO_CROP_HINT,
                        // Its own, for the avatar's reason one line down: a square-locked
                        // crop cannot leave transparent space around the mark, which is what
                        // the logo's hint spends its last sentence explaining how to do.
                        favicon: ResourcesIds.BRANDING_FAVICON_CROP_HINT,
                        // The only one that differs, and it has to: the logo's hint ends by
                        // explaining how to leave transparent space around the mark, which is
                        // the one thing a square-locked crop into a circle cannot do.
                        avatar: ResourcesIds.PROFILE_PICTURE_CROP_HINT,
                    }))}
                </Typography>

                {isOpaque && !isHero && !isAvatar && (
                    <Alert severity="info" sx={warningStyles}>
                        {translate(isWatermark
                            ? ResourcesIds.BRANDING_WATERMARK_CROP_OPAQUE_WARNING
                            : ResourcesIds.BRANDING_LOGO_CROP_OPAQUE_WARNING)}
                    </Alert>
                )}

                {failed && (
                    <Alert severity="error" sx={warningStyles}>
                        {failed}
                    </Alert>
                )}

                <Box sx={bodyStyles}>
                    <Box>
                        <Box
                            sx={stageStyles}
                            style={{ width: stage.width, height: stage.height }}
                            onPointerDown={startDrag('pan')}
                            onPointerMove={onPointerMove}
                            onPointerUp={endDrag}
                            onPointerCancel={endDrag}
                        >
                            {objectUrl && (
                                <Box
                                    component="img"
                                    src={objectUrl}
                                    alt=""
                                    data-testid="crop-stage-image"
                                    onLoad={onImageLoad}
                                    sx={stageImageStyles}
                                    style={crop ? drawnStyle(crop) : { visibility: 'hidden' }}
                                />
                            )}

                            {crop && (
                                <>
                                    <Box
                                        sx={shadeStyles}
                                        style={{ left: 0, top: 0, right: 0, height: crop.frame.y }}
                                    />
                                    <Box
                                        sx={shadeStyles}
                                        style={{
                                            left: 0,
                                            top: crop.frame.y + crop.frame.height,
                                            right: 0,
                                            bottom: 0,
                                        }}
                                    />
                                    <Box
                                        sx={shadeStyles}
                                        style={{
                                            left: 0,
                                            top: crop.frame.y,
                                            width: crop.frame.x,
                                            height: crop.frame.height,
                                        }}
                                    />
                                    <Box
                                        sx={shadeStyles}
                                        style={{
                                            left: crop.frame.x + crop.frame.width,
                                            top: crop.frame.y,
                                            right: 0,
                                            height: crop.frame.height,
                                        }}
                                    />

                                    <Box
                                        sx={frameStyles}
                                        style={{
                                            left: crop.frame.x,
                                            top: crop.frame.y,
                                            width: crop.frame.width,
                                            height: crop.frame.height,
                                        }}
                                    />

                                    {CORNERS.map((corner) => (
                                        <Box
                                            key={corner}
                                            data-testid={`crop-handle-${corner}`}
                                            sx={handleStyles}
                                            style={handlePosition(crop, corner)}
                                            onPointerDown={startDrag(corner)}
                                        />
                                    ))}
                                </>
                            )}

                            {!crop && !failed && (
                                <Box sx={loadingStyles}>
                                    <CircularProgress size={28} />
                                </Box>
                            )}
                        </Box>

                        <Box sx={controlsStyles}>
                            <Typography variant="caption" color="text.secondary">
                                {translate(ResourcesIds.BRANDING_LOGO_CROP_ZOOM)}
                            </Typography>

                            {/*
                             * The track is `sliderToZoom`'s 0..1, not the zoom itself, and it
                             * carries one mark: fit. Below it the frame outruns the image and
                             * the crop gains margin, above it the crop is a detail of the
                             * image, and an admin wants to find that boundary again.
                             */}
                            <Slider
                                sx={zoomSliderStyles}
                                value={zoomToSlider(crop?.zoom ?? 1)}
                                min={0}
                                max={1}
                                step={0.001}
                                marks={FIT_MARK}
                                disabled={!crop}
                                aria-label={translate(ResourcesIds.BRANDING_LOGO_CROP_ZOOM)}
                                onChange={(_, value) =>
                                    setCrop((state) =>
                                        state ? zoomTo(state, sliderToZoom(value as number)) : state
                                    )
                                }
                            />

                            <Button
                                size="small"
                                color="inherit"
                                disabled={!crop}
                                onClick={() =>
                                    setCrop((state) =>
                                        state
                                            ? initialCrop(state.natural, state.stage,
                                                aspectFor(variant))
                                            : state
                                    )
                                }
                            >
                                {translate(ResourcesIds.BRANDING_LOGO_CROP_RESET)}
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={previewsStyles}>
                        <Box>
                            <Typography variant="caption" sx={previewLabelStyles}>
                                {translate(cropKey(variant, {
                                    logo: ResourcesIds.BRANDING_LOGO_CROP_PREVIEW_TRANSPARENCY,
                                    watermark: ResourcesIds.BRANDING_LOGO_CROP_PREVIEW_TRANSPARENCY,
                                    hero: ResourcesIds.HOME_HERO_CROP_PREVIEW_CROP,
                                    favicon: ResourcesIds.BRANDING_LOGO_CROP_PREVIEW_TRANSPARENCY,
                                    avatar: ResourcesIds.PROFILE_PICTURE_CROP_PREVIEW_CROP,
                                }))}
                            </Typography>

                            {/* A chequerboard answers "where is this transparent", which is a
                                question about a logo and not about a photograph. A circle
                                answers the avatar's question instead: the crop is locked to a
                                square, so the mask takes nothing off and what is drawn here is
                                exactly what gets stored. */}
                            <Box sx={firstPreviewStyles}>
                                {objectUrl && firstPreview && (
                                    <Box
                                        sx={previewWindowStyles}
                                        style={firstPreview.window}
                                    >
                                        <Box
                                            component="img"
                                            src={objectUrl}
                                            alt=""
                                            sx={previewImageStyles}
                                            style={firstPreview.image}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={previewLabelStyles}>
                                {translate(cropKey(variant, {
                                    logo: ResourcesIds.BRANDING_LOGO_CROP_PREVIEW_HEADER,
                                    watermark: ResourcesIds.BRANDING_WATERMARK_CROP_PREVIEW_PAGE,
                                    hero: ResourcesIds.HOME_HERO_CROP_PREVIEW_BAND,
                                    favicon: ResourcesIds.BRANDING_FAVICON_CROP_PREVIEW_TAB,
                                    avatar: ResourcesIds.PROFILE_PICTURE_CROP_PREVIEW_HEADER,
                                }))}
                            </Typography>

                            {isHero ? (
                                /* The question this preview exists to answer: with the page's
                                   own wash over it, can the headline still be read? */
                                <Box sx={heroPreviewStyles}>
                                    {objectUrl && hero && (
                                        <Box sx={previewWindowStyles} style={hero.window}>
                                            <Box
                                                component="img"
                                                src={objectUrl}
                                                alt=""
                                                sx={previewImageStyles}
                                                style={hero.image}
                                            />
                                        </Box>
                                    )}

                                    <Box sx={heroPreviewScrimStyles} />

                                    <Typography variant="body2" sx={heroPreviewTextStyles}>
                                        {translate(ResourcesIds.HOME_HERO_CROP_SAMPLE_HEADLINE)}
                                    </Typography>
                                </Box>
                            ) : isAvatar ? (
                                /* The real app bar, with the avatar at the 36px it is drawn
                                   at there. The question this answers is whether the crop is
                                   still recognisable once it is that small -- which a 176px
                                   preview cannot tell anybody. */
                                <Box sx={headerPreviewBackdropStyles}>
                                    <Box sx={headerPreviewBarStyles}>
                                        <Box sx={headerAvatarNameStyles} />

                                        <Box sx={headerAvatarPreviewStyles}>
                                            {objectUrl && headerAvatar && (
                                                <Box
                                                    sx={previewWindowStyles}
                                                    style={headerAvatar.window}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={objectUrl}
                                                        alt=""
                                                        sx={previewImageStyles}
                                                        style={headerAvatar.image}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            ) : isFavicon ? (
                                /* A browser tab, with the icon at the 16px a browser draws
                                   it at. The same question the avatar's app-bar preview
                                   asks, and the only one that matters here: at that size,
                                   is this still recognisable as the clinic's mark? */
                                <Box sx={tabPreviewBackdropStyles}>
                                    <Box sx={tabPreviewStyles}>
                                        <Box sx={tabPreviewIconStyles}>
                                            {objectUrl && tabIcon && (
                                                <Box
                                                    sx={previewWindowStyles}
                                                    style={tabIcon.window}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={objectUrl}
                                                        alt=""
                                                        sx={previewImageStyles}
                                                        style={tabIcon.image}
                                                    />
                                                </Box>
                                            )}
                                        </Box>

                                        <Box sx={tabPreviewTitleStyles} />
                                    </Box>
                                </Box>
                            ) : isWatermark ? (
                                <Box sx={pagePreviewStyles}>
                                    <Box sx={pagePreviewLinesStyles} />

                                    {objectUrl && page && (
                                        <Box
                                            sx={pagePreviewMarkStyles}
                                            style={{ opacity: PREVIEW_OPACITY }}
                                        >
                                            <Box sx={previewWindowStyles} style={page.window}>
                                                <Box
                                                    component="img"
                                                    src={objectUrl}
                                                    alt=""
                                                    sx={previewImageStyles}
                                                    style={page.image}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={headerPreviewBackdropStyles}>
                                    <Box sx={headerPreviewBarStyles}>
                                        {objectUrl && header && (
                                            <Box sx={previewWindowStyles} style={header.window}>
                                                <Box
                                                    component="img"
                                                    src={objectUrl}
                                                    alt=""
                                                    sx={previewImageStyles}
                                                    style={header.image}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
                <Button color="inherit" onClick={onCancel} disabled={isBusy}>
                    {translate(ResourcesIds.BRANDING_LOGO_CROP_CANCEL)}
                </Button>

                <Button variant="contained" onClick={onSave} disabled={!crop || isBusy}>
                    {translate(cropKey(variant, {
                        logo: ResourcesIds.BRANDING_LOGO_CROP_SAVE,
                        watermark: ResourcesIds.BRANDING_WATERMARK_CROP_SAVE,
                        hero: ResourcesIds.HOME_HERO_CROP_SAVE,
                        favicon: ResourcesIds.BRANDING_FAVICON_CROP_SAVE,
                        avatar: ResourcesIds.PROFILE_PICTURE_CROP_SAVE,
                    }))}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
